import React, { FC, memo, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";
import { RoutedTabPanels } from "router";

import { Section } from "@/components/Section";
import { TabSections, TabSelect, TTabOption } from "@/components/Tabs";
import { UserContext } from "@/components/User";
import { MEDIA_SIZE_SM } from "@/core/media";
import { Col, Flex, Row, Tabs } from "@/eworkitComponents";
import { Container } from "@/views/components/Container";
import { CompanyBankingSettings } from "@/views/Settings/CompanySettings/CompanyData/CompanyBankingSettings";
import { CompanyInsuranceSettings } from "@/views/Settings/CompanySettings/CompanyData/CompanyInsuranceSettings";
import { ConsultantProfilesVisibilitySettings } from "@/views/Settings/CompanySettings/CompanyData/ConsultantProfilesVisibilitySettings";

import { PageHeader } from "../../components/PageHeader";
import { CompanyDataSettings } from "./CompanyData";
import { CompanyContactSettings } from "./CompanyData/CompanyContactSettings";

export const CompanySettings: FC = memo(() => {
  const User = useContext(UserContext);
  const companyId = User.contextCompany?.id;
  const isManager = User.hasCompanyManagerPrivileges();

  const { t } = useTranslation("session");

  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const tabOptions: TTabOption[] = useMemo(
    () => [
      {
        type: "group",
        options: [
          { type: "option", value: "company-data", label: t("USERS.SETTINGS.COMPANY_DATA.TAB_NAME") },
          {
            type: "option",
            value: "company-contact-details",
            label: t("USERS.SETTINGS.COMPANY_CONTACT_DETAILS.TAB_NAME"),
          },
          { type: "option", value: "banking-details", label: t("USERS.SETTINGS.BANKING_DETAILS.TAB_NAME") },
          { type: "option", value: "insurance", label: t("USERS.SETTINGS.INSURANCE_DETAILS.TAB_NAME") },
          {
            type: "option",
            value: "consultant-profile-visibility",
            label: t("USERS.SETTINGS.CONSULTANT_PROFILES_VISIBILITY.TAB_NAME"),
          },
        ],
        hide: !isManager,
      },
    ],
    [t, isManager],
  );
  const tabPanels = useMemo(
    () => ({
      "company-data": () => <CompanyDataSettings />,
      "company-contact-details": () => <CompanyContactSettings />,
      "banking-details": () => <CompanyBankingSettings />,
      insurance: () => <CompanyInsuranceSettings />,
      "consultant-profile-visibility": () => <ConsultantProfilesVisibilitySettings />,
    }),
    [companyId],
  );
  return (
    <>
      {<PageHeader header={t("USERS.SETTINGS.COMPANY_SETTINGS.COMPANY_SETTINGS")} />}
      <Container padding={isSmallScreen ? ["sm", "sm", "md"] : ["md", "md", "xxl", "md"]}>
        <Row size="md" direction={isSmallScreen ? "column" : "row"}>
          <Col width={{ xxs: 12, sm: 4 }}>
            <Flex direction="column" innerMargin="md">
              {isSmallScreen ? (
                <Section borderRadius padding={["lg"]} innerMargin="md">
                  <TabSelect pathName="company.settings" routeParam="tab" options={tabOptions} />
                </Section>
              ) : (
                <TabSections
                  pathName="company.settings"
                  routeParam="tab"
                  options={tabOptions}
                  variant="vertical-icon-tab"
                  innerMargin="sm"
                />
              )}
            </Flex>
          </Col>

          <Col width={{ xxs: 12, sm: 8 }}>
            <Tabs>
              <RoutedTabPanels routeParam="tab" views={tabPanels} />
            </Tabs>
          </Col>
        </Row>
      </Container>
    </>
  );
});
